import { getToken } from "@gtmhub/auth";
import { LoggingService } from "@gtmhub/core/logging";
import { IAppConfig, EnvironmentService } from "@gtmhub/env";
import { IRootScopeService } from "angular";
import ReconnectingWebSocket from "reconnecting-websocket";
import { extractOriginalShape, IShapeReactionResponse } from ".";
import { AccountResolverService } from "../state/accountResolverService";
import { WhiteboardEvents } from "./events";
import { IWhiteboardStateChangeNotifier, IWhiteboardStateChange } from "./rendering/state/state-change-notifier";
import { FeatureToggleV2Service } from "@gtmhub/featureToggle";
import { IReactionSettings } from "./models";

interface ICollaborationData {
  clients: {
    clientId: string;
    connectionId: string;
  }[];
  lockedShapes: { [shapeId: string]: string };
}

export interface IStateChangeSuccess {
  status: "success";
  addedShapeIds?: { [shapeId: string]: string };
}

export interface IStateChangeSocketMessage {
  action: "stateChangedV2";
  payload: IWhiteboardStateChange[] | IStateChangeSuccess;
  clientId?: string;
  connectionId?: string;
}
export interface ICollaborateWhiteboardSocketMessage {
  action: "collaborate";
  payload: ICollaborationData;
  clientId?: string;
  connectionId?: string;
}
export interface IShapeLockedSocketMessage {
  action: "shapeLocked";
  payload: IGeneralShapeSocketMessage;
  clientId?: string;
  connectionId?: string;
}

export interface IShapeUnlockedSocketMessage {
  action: "shapeUnlocked";
  payload: IGeneralShapeSocketMessage;
  clientId?: string;
  connectionId?: string;
}

export interface IGeneralShapeSocketMessage {
  shapeId: string;
}

export interface IReactionSettingsChangeSocketMessage {
  action: "settingsChanged";
  clientId?: string;
  payload: IReactionSettings;
}

export interface IShapeReactionSocketMessage {
  action: "shapeReaction";
  clientId?: string;
  payload: IShapeReactionResponse;
}

type WhiteboardSocketMessage =
  | IStateChangeSocketMessage
  | ICollaborateWhiteboardSocketMessage
  | IShapeLockedSocketMessage
  | IShapeUnlockedSocketMessage
  | IReactionSettingsChangeSocketMessage
  | IShapeReactionSocketMessage;

export class WhiteboardSocketService implements IWhiteboardStateChangeNotifier {
  private socket: ReconnectingWebSocket;
  private events: WhiteboardEvents;
  private loggingService: LoggingService;
  private apiVersion: "v1" | "v2";

  static $inject = ["$rootScope", "EnvironmentService", "AccountResolverService", "appConfig", "FeatureToggleV2Service"];

  constructor(
    $rootScope: IRootScopeService,
    private env: EnvironmentService,
    private accountResolverService: AccountResolverService,
    private appConfig: IAppConfig,
    private featureToggleService: FeatureToggleV2Service
  ) {
    this.events = new WhiteboardEvents($rootScope);
    this.loggingService = new LoggingService(this.appConfig.logging, "spa");
  }

  initializeSocket(whiteboardId: string): Promise<void> {
    return this.featureToggleService.isFeatureAvailable("wbws-v2").then((enabled) => {
      this.apiVersion = enabled ? "v2" : "v1";

      const accountId = this.accountResolverService.getAccountId();
      const urlProvider = async () => {
        const token = getToken();
        return this.env.getWbSocketsEndpointForApiVersion(this.apiVersion, `/${whiteboardId}/ws?authorization=${token}&accountId=${accountId}`);
      };

      this.socket = new ReconnectingWebSocket(urlProvider, null, { connectionTimeout: 10000 });
      this.socket.onmessage = (event) => this.onMessage(event);
      this.socket.onerror = (event) => {
        const url = this.env.getWbSocketsEndpointForApiVersion(this.apiVersion, `/${whiteboardId}/ws?accountId=${accountId}`);
        this.loggingService.logSocketError(event, url);
        this.events.broadcastSocketError();
      };
      // check whether on close event needs special handler, different from the one for error
      this.socket.onclose = (event) => {
        if (event.reason !== "Component destroyed") this.events.broadcastSocketError();
      };
      this.socket.onopen = () => this.events.broadcastSocketOpened();
    });
  }

  stateChange(payload: IWhiteboardStateChange[]): void {
    for (let i = 0; i < payload.length; i++) {
      const shape = payload[i].value;
      if ((shape.type === "alert" && shape.original) || shape.type === "async") {
        payload[i].value = extractOriginalShape(shape);
      }
    }

    const message: IStateChangeSocketMessage = { action: "stateChangedV2", payload };
    this.socket.send(JSON.stringify(message));
  }

  lockShape(payload: IGeneralShapeSocketMessage): void {
    const message: IShapeLockedSocketMessage = { action: "shapeLocked", payload };
    this.socket.send(JSON.stringify(message));
  }

  unlockShape(payload?: IGeneralShapeSocketMessage): void {
    const message: IShapeUnlockedSocketMessage = { action: "shapeUnlocked", payload };
    this.socket.send(JSON.stringify(message));
  }

  close(code?: number, reason?: string): void {
    this.socket.close(code, reason);
  }

  private onMessage(event: MessageEvent): void {
    const message: WhiteboardSocketMessage = JSON.parse(event.data);
    switch (message.action) {
      case "collaborate":
        this.events.broadcastCollaborate(message);
        break;
      case "stateChangedV2":
        this.events.broadcastForeignStateChange(message);
        break;
      case "shapeLocked":
        this.events.broadcastShapeLocked(message);
        break;
      case "shapeUnlocked":
        this.events.broadcastShapeUnlocked(message);
        break;
      case "settingsChanged":
        this.events.broadcastReactionSettingsChanged(message);
        break;
      case "shapeReaction":
        this.events.broadcastShapeReaction(message);
        break;
    }
  }
}
